import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import copy from 'copy-to-clipboard';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Text, TextProps } from '@lifeomic/chroma-react/components/Text';
import { Copy, Info } from '@lifeomic/chromicons';
import type { WithPlatformDefaults } from '@lifeomic/service-clients/fhir-client';

import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Member } from './utils';
import { MemberContactEditModal } from './MemberContactEditModal';
import {
  memberOpsRequestErrorMessage,
  type MemberContactField,
} from './memberContactEdit';
import {
  MEMBER_CONTACT_SYNC_QUERY_KEY,
  type MemberContactSyncStatus,
  memberContactSyncStatusPath,
  syncMemberContactPath,
} from './memberContactSync';
import { MemberContactSyncIndicator } from './MemberContactSyncIndicator';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import { client } from '../../util/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatOnboardMemberError,
  MEMBER_APP_PERSON_USERNAME_QUERY_KEY,
  POST_MEMBER_ONBOARD_APP,
} from './memberAppUsername';
import {
  Alert,
  AlertBody,
  AlertIcon,
} from '@lifeomic/chroma-react/components/Alert';
import { getTestProps } from '@lifeomic/phc-web-toolkit/src/util/testProps';
import { Button } from '@lifeomic/chroma-react/components/Button';

const messages = defineMessages({
  preferredName: {
    id: 'IndividualOnboardingView.preferredName',
    defaultMessage: '{preferredName}',
  },
  emailLabel: {
    id: 'IndividualOnboardingView.emailLabel',
    defaultMessage: 'Email',
  },
  email: {
    id: 'IndividualOnboardingView.email',
    defaultMessage: '{email}',
  },
  phoneLabel: {
    id: 'IndividualOnboardingView.phoneLabel',
    defaultMessage: 'Phone',
  },
  phone: {
    id: 'IndividualOnboardingView.phone',
    defaultMessage: '{phone}',
  },
  genderLabel: {
    id: 'IndividualOnboardingView.genderLabel',
    defaultMessage: 'Gender',
  },
  gender: {
    id: 'IndividualOnboardingView.gender',
    defaultMessage: '{gender}',
  },
  elationId: {
    id: 'IndividualOnboardingView.elationId',
    defaultMessage: 'Elation ID',
  },
  elationStatus: {
    id: 'IndividualOnboardingView.elationStatus',
    defaultMessage: 'Elation Status',
  },
  copyToClipboard: {
    id: 'IndividualOnboardingView.copyToClipboard',
    defaultMessage: 'Copy to clipboard',
  },
  dob: {
    id: 'IndividualOnboardingView.dob',
    defaultMessage: 'Date of Birth',
  },
  centerLabel: {
    id: 'IndividualOnboardingView.centerLabel',
    defaultMessage: 'Center',
  },
  usernameLabel: {
    id: 'MemberInfo.usernameLabel',
    defaultMessage: 'Username',
  },
  editContactHint: {
    id: 'MemberInfo.editContactHint',
    defaultMessage: 'Click to edit',
  },
  notOnboardedBanner: {
    id: 'IndividualOnboardingView.notOnboardedBanner',
    defaultMessage: 'This member has not yet been onboarded to the app.',
  },
  onboardThemNow: {
    id: 'IndividualOnboardingView.onboardThemNow',
    defaultMessage: 'Onboard them now.',
  },
  retry: {
    id: 'IndividualOnboardingView.retry',
    defaultMessage: 'Retry onboarding',
  },
  onboardSuccess: {
    id: 'IndividualOnboardingView.onboardSuccess',
    defaultMessage: 'App onboarding successful.',
  },
  onboardError: {
    id: 'IndividualOnboardingView.onboardError',
    defaultMessage:
      'Unable to start onboarding. Please review the errors and try again.',
  },
  syncSuccess: {
    id: 'MemberInfo.syncSuccess',
    defaultMessage: 'App login synced to the snapshot value.',
  },
  syncError: {
    id: 'MemberInfo.syncError',
    defaultMessage: 'Unable to sync. Try again.',
  },
});

const useStyles = makeStyles(theme => ({
  patientInfo: {
    display: 'grid',
    gap: `0 ${theme.spacing(1.5)}`,
    gridTemplateColumns: '175px 1fr',
  },
  label: {
    color: theme.palette.text.hint,
    fontWeight: theme.typography.fontWeightBold,
  },
  divider: {
    gridColumn: '-1 / 1',
  },
  copyButtonContainer: {
    '& button': {
      opacity: 0,
    },
    '&:hover button': {
      opacity: 1,
    },
    transition: 'opacity 0.3s ease-in-out',
  },
  copyButton: {
    padding: 0,
  },
  editableValue: {
    cursor: 'pointer',
    textDecoration: 'underline',
    textDecorationStyle: 'dotted',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
    },
  },
}));

const CopyableText: React.FC<
  Omit<TextProps, 'children'> & {
    value: string | undefined;
    linkTo?: string;
    onValueClick?: () => void;
    editHint?: string;
  }
> = ({ value: _value, linkTo, onValueClick, editHint, ...textProps }) => {
  const { formatMessage } = useIntl();
  const styles = useStyles();
  const value = _value || '';

  const textContent = linkTo ? (
    <a target="_blank" rel="noreferrer" href={linkTo}>
      <Text {...textProps}>{value}</Text>
    </a>
  ) : onValueClick ? (
    <Text
      {...textProps}
      role="button"
      tabIndex={0}
      className={styles.editableValue}
      title={editHint}
      onClick={onValueClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onValueClick();
        }
      }}
    >
      {value}
    </Text>
  ) : (
    <Text {...textProps}>{value}</Text>
  );

  return (
    <Box
      direction="row"
      justify="space-between"
      align="center"
      className={styles.copyButtonContainer}
    >
      {textContent}
      <IconButton
        icon={Copy}
        onClick={e => {
          e.stopPropagation();
          copy(value);
        }}
        aria-label={formatMessage(messages.copyToClipboard)}
        disabled={!value}
        size={0}
        className={styles.copyButton}
      />
    </Box>
  );
};

export type MemberInfoProps = {
  member: Member;
  patient: WithPlatformDefaults<fhir3.Patient>;
  memberOpsEnabled?: boolean;
  onMemberContactSaved?: () => Promise<void>;
  /** Fountain Life app username from linked Person (member operations); empty if not onboarded. */
  appUsername?: string;
  /** While loading Person search for app username, show a compact loader in the Username row. */
  isAppUsernameLoading?: boolean;
};

export const MemberInfo: React.FC<MemberInfoProps> = props => {
  const styles = useStyles();
  const [notify] = useNotify();
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const [editingField, setEditingField] =
    React.useState<MemberContactField | null>(null);

  const canEditContact =
    Boolean(props.memberOpsEnabled) && Boolean(props.patient);

  const [onboardErrorMessage, setOnboardErrorMessage] = useState<string | null>(
    null,
  );
  const [onboardErrorMessageHowToFix, setOnboardErrorMessageHowToFix] =
    useState<string | null>(null);

  const editHint = formatMessage(messages.editContactHint);

  const showNotOnboardedBanner =
    props.memberOpsEnabled &&
    !props.isAppUsernameLoading &&
    props.appUsername?.trim() === '';

  const onboardMemberMutation = useMutation({
    mutationFn: async () => {
      const response = await client.post(POST_MEMBER_ONBOARD_APP, {
        elationId: props.member.elationId,
      });
      // The onboard-member endpoint runs synchronously and reports
      // recoverable failures as `200 { ok: false, error, howToFix }` so the
      // UI can show a remediation step. Convert that into a thrown error so
      // React Query treats it as a failed mutation.
      const { error, howToFix } = formatOnboardMemberError(response.data) ?? {
        error: '',
        howToFix: '',
      };
      setOnboardErrorMessage(error);
      setOnboardErrorMessageHowToFix(howToFix);
      if (error) {
        throw new Error(error);
      }
    },
    onSuccess: async () => {
      notify(formatMessage(messages.onboardSuccess), 'success');
      await queryClient.invalidateQueries({
        queryKey: MEMBER_APP_PERSON_USERNAME_QUERY_KEY,
      });
      await props.onMemberContactSaved?.();
    },
    onError: (_: unknown) => {
      notify(formatMessage(messages.onboardError), 'error');
    },
  });

  const patientId = props.patient?.id;
  const isOnboarded = Boolean(props.appUsername?.trim());

  const contactSyncQuery = useQuery({
    queryKey: [...MEMBER_CONTACT_SYNC_QUERY_KEY, patientId],
    queryFn: async (): Promise<MemberContactSyncStatus> => {
      const response = await client.get<MemberContactSyncStatus>(
        memberContactSyncStatusPath(patientId as string),
      );
      return response.data;
    },
    enabled: Boolean(props.memberOpsEnabled && patientId && isOnboarded),
  });

  const syncMutation = useMutation({
    mutationFn: async (field: MemberContactField) => {
      await client.post(syncMemberContactPath(patientId as string), { field });
    },
    onSuccess: async () => {
      notify(formatMessage(messages.syncSuccess), 'success');
      await queryClient.invalidateQueries({
        queryKey: MEMBER_CONTACT_SYNC_QUERY_KEY,
      });
      await props.onMemberContactSaved?.();
    },
    onError: (err: unknown) => {
      notify(
        memberOpsRequestErrorMessage(err) ?? formatMessage(messages.syncError),
        'error',
      );
    },
  });

  const contactSyncStatus = contactSyncQuery.data;
  const renderSyncIndicator = (field: MemberContactField) =>
    contactSyncStatus ? (
      <MemberContactSyncIndicator
        field={field}
        status={contactSyncStatus[field]}
        onSync={() => syncMutation.mutate(field)}
        isSyncing={syncMutation.isLoading && syncMutation.variables === field}
      />
    ) : null;

  return (
    <>
      {props.member.preferredName ? (
        <Text
          onClick={() => {
            props.member.preferredName && copy(props.member.preferredName);
          }}
        >
          {formatMessage(messages.preferredName, {
            preferredName: props.member.preferredName,
          })}
        </Text>
      ) : null}
      <CopyableText size="headline" weight="bold" value={props.member.name} />
      {showNotOnboardedBanner ? (
        <Alert
          statusType="info"
          fullWidth
          {...getTestProps('member-not-onboarded-banner')}
        >
          <AlertIcon icon={Info} />
          <AlertBody>
            <Box direction="row" align="center" gap={1} flexWrap>
              <Text size="subbody">
                {formatMessage(messages.notOnboardedBanner)}
              </Text>
              {onboardErrorMessage ? (
                <Text size="subbody">{onboardErrorMessage}</Text>
              ) : null}
              {onboardErrorMessageHowToFix ? (
                <Text size="subbody">{onboardErrorMessageHowToFix}</Text>
              ) : null}
              <Button
                variant="text"
                disabled={onboardMemberMutation.isLoading}
                onClick={() => onboardMemberMutation.mutate()}
                {...getTestProps('member-app-onboard-action')}
              >
                {formatMessage(
                  onboardErrorMessage
                    ? messages.retry
                    : messages.onboardThemNow,
                )}
              </Button>
            </Box>
          </AlertBody>
        </Alert>
      ) : null}

      <Box className={styles.patientInfo}>
        <Divider className={styles.divider} />
        <Text className={styles.label} size="subbody">
          {formatMessage(messages.elationId)}
        </Text>
        <CopyableText
          size="subbody"
          value={props.member.elationId}
          linkTo={props.member.elationChartUrl}
        />

        <Divider className={styles.divider} />
        <Text className={styles.label} size="subbody">
          {formatMessage(messages.elationStatus)}
        </Text>
        <Text size="subbody">{props.member.elationStatus}</Text>

        <Divider className={styles.divider} />
        <Text className={styles.label} size="subbody">
          {formatMessage(messages.dob)}
        </Text>
        <Text size="subbody">{props.member.dateOfBirth}</Text>

        <Divider className={styles.divider} />
        <Text className={styles.label} size="subbody">
          {formatMessage(messages.emailLabel)}
        </Text>
        <Box direction="column" gap={1}>
          <CopyableText
            size="subbody"
            value={props.member.email}
            onValueClick={
              canEditContact ? () => setEditingField('email') : undefined
            }
            editHint={editHint}
          />
          {renderSyncIndicator('email')}
        </Box>
        <Divider className={styles.divider} />
        <Text className={styles.label} size="subbody">
          {formatMessage(messages.phoneLabel)}
        </Text>
        <Box direction="column" gap={1}>
          <CopyableText
            size="subbody"
            value={props.member.phone}
            onValueClick={
              canEditContact ? () => setEditingField('phone') : undefined
            }
            editHint={editHint}
          />
          {renderSyncIndicator('phone')}
        </Box>
        <Divider className={styles.divider} />
        <Text className={styles.label} size="subbody">
          {formatMessage(messages.usernameLabel)}
        </Text>
        {props.isAppUsernameLoading ? (
          <DotLoader size={0} />
        ) : (
          <CopyableText size="subbody" value={props.appUsername ?? ''} />
        )}
        <Divider className={styles.divider} />
        <Text className={styles.label} size="subbody">
          {formatMessage(messages.genderLabel)}
        </Text>
        <Text size="subbody">
          {formatMessage(messages.gender, {
            gender: props.member.gender,
          })}
        </Text>
        <Divider className={styles.divider} />
        <Text className={styles.label} size="subbody">
          {formatMessage(messages.centerLabel)}
        </Text>
        <Text size="subbody">{props.member.centerName}</Text>
        <Divider className={styles.divider} />
      </Box>

      {canEditContact && props.patient ? (
        <MemberContactEditModal
          isOpen={Boolean(editingField)}
          onDismiss={() => setEditingField(null)}
          field={editingField}
          memberName={props.member.name}
          patient={props.patient}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: MEMBER_CONTACT_SYNC_QUERY_KEY,
            });
            await props.onMemberContactSaved?.();
          }}
        />
      ) : null}
    </>
  );
};
